import { APICustomer, deleteCustomer } from "../api/customers";

// Customer deletes wait out an undo period before they really happen. The
// countdown lives here, not in a page, so it keeps running if the user
// moves to another page, and it is saved on the device, so closing or
// reloading the app still finishes (or still lets you undo) the delete.

export const UNDO_MS = 10000;
const KEY = "pendingCustomerDeletes";

export type PendingCustomer = Pick<APICustomer, "id" | "firstName" | "lastName">;
type Pending = { deadline: number; customers: PendingCustomer[] };

let pending: Pending | undefined;
let timer: ReturnType<typeof setTimeout> | undefined;
let running: Promise<string[]> | undefined;
const listeners = new Set<() => void>();

function save() {
  try {
    if (pending) localStorage.setItem(KEY, JSON.stringify(pending));
    else localStorage.removeItem(KEY);
  } catch {
    // private browsing: works while the app stays open
  }
}

function emit() {
  listeners.forEach((fn) => fn());
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

const NONE: PendingCustomer[] = [];
export function getPending(): PendingCustomer[] {
  return pending?.customers || NONE;
}

// Delete now. Resolves with the names that couldn't be deleted.
export function commitPending(): Promise<string[]> {
  if (running) return running;
  clearTimeout(timer);
  const list = pending?.customers || [];
  if (!list.length) return Promise.resolve([]);
  running = (async () => {
    const failed: string[] = [];
    for (const c of list) {
      try {
        await deleteCustomer(c.id || "");
      } catch {
        failed.push(`${c.firstName} ${c.lastName}`.trim());
      }
    }
    pending = undefined;
    save();
    running = undefined;
    emit();
    return failed;
  })();
  return running;
}

export async function scheduleDelete(customers: PendingCustomer[]) {
  // A second delete inside the undo time finishes the first one.
  if (pending) await commitPending();
  pending = {
    deadline: Date.now() + UNDO_MS,
    customers: customers.map((c) => ({ id: c.id, firstName: c.firstName, lastName: c.lastName })),
  };
  save();
  timer = setTimeout(commitPending, UNDO_MS);
  emit();
}

// Cancel the waiting delete. Returns how many customers were kept.
export function undoDelete(): number {
  if (!pending || running) return 0;
  clearTimeout(timer);
  const n = pending.customers.length;
  pending = undefined;
  save();
  emit();
  return n;
}

// Resume a delete confirmed before the app was closed or reloaded.
function restore() {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) || "null");
    if (!saved || !Array.isArray(saved.customers) || !saved.customers.length) return;
    pending = { deadline: Number(saved.deadline) || 0, customers: saved.customers };
    // After the app (and its database) has finished starting.
    timer = setTimeout(commitPending, Math.max(pending.deadline - Date.now(), 500));
  } catch {
    // nothing saved
  }
}
restore();
